import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EmailIcon from '@mui/icons-material/Email';
import EventIcon from '@mui/icons-material/Event';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import PaymentsIcon from '@mui/icons-material/Payments';
import PhoneIcon from '@mui/icons-material/Phone';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import StorefrontIcon from '@mui/icons-material/Storefront';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import moment from 'moment';
import DashboardChartCard from '../components/common/DashboardChartCard';
import { getHallVendorDetail } from '../modules/hall/api';
import HallModuleNav, { HALL_ACCENT } from '../modules/hall/components/HallModuleNav';
import { getHallApiErrorMessage } from '../modules/hall/utils/hallErrors';
import { formatHallCurrency, roundHallMoney } from '../modules/hall/utils/hallFinancials';
import { DataTable } from '../shared';
import {
  DASHBOARD_ACCENT,
  getDashboardDarkSurfaceTokens,
  getDashboardPageShellSx,
} from '../utils/dashboardSurfaceSx';

const SECONDARY = '#8ac9ff';
const WARNING = '#fbbf24';
const DUE_COLOR = '#f97316';
const PROFIT = '#34d399';
const METHOD_COLORS = [HALL_ACCENT, SECONDARY, WARNING, '#c4b5fd', PROFIT, DUE_COLOR, '#fb7185', '#67e8f9'];

function formatPhone(vendor) {
  const code = String(vendor?.phone_country_code || '').trim();
  const number = String(vendor?.phone || '').trim();
  if (!number) return '—';
  return code ? `${code} ${number}` : number;
}

function formatDate(value) {
  if (!value) return '—';
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('DD MMM YYYY') : String(value);
}

function formatDateTime(value) {
  if (!value) return '—';
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('DD MMM YYYY, hh:mm A') : String(value);
}

function vendorInitials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'V';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function parseServiceTags(value) {
  if (Array.isArray(value)) return value.map((item) => String(item || '').trim()).filter(Boolean);
  return String(value || '')
    .split(/[,\n]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function statusChipSx(status, isDark) {
  const key = String(status || '').toLowerCase();
  if (key === 'confirmed' || key === 'completed' || key === 'active') {
    return {
      color: isDark ? '#bbf7d0' : '#166534',
      bgcolor: alpha('#22c55e', isDark ? 0.2 : 0.12),
      fontWeight: 800,
    };
  }
  if (key === 'tentative') {
    return {
      color: isDark ? '#bfdbfe' : '#1d4ed8',
      bgcolor: alpha('#3b82f6', isDark ? 0.2 : 0.12),
      fontWeight: 800,
    };
  }
  if (key === 'cancelled' || key === 'inactive') {
    return {
      color: isDark ? '#fecaca' : '#991b1b',
      bgcolor: alpha('#ef4444', isDark ? 0.2 : 0.12),
      fontWeight: 800,
    };
  }
  return {
    color: isDark ? '#cbd5e1' : '#475569',
    bgcolor: alpha('#64748b', isDark ? 0.2 : 0.12),
    fontWeight: 800,
  };
}

function MetricTile({ title, value, subtitle, icon, color, isDark }) {
  return (
    <Paper
      elevation={0}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        height: '100%',
        borderRadius: '16px',
        border: `1px solid ${alpha(color, isDark ? 0.28 : 0.2)}`,
        background: isDark
          ? `linear-gradient(155deg, ${alpha('#111827', 0.96)} 0%, ${alpha('#0f172a', 0.94)} 55%, ${alpha(color, 0.12)} 100%)`
          : `linear-gradient(155deg, #ffffff 0%, #f8fafc 50%, ${alpha(color, 0.12)} 100%)`,
        boxShadow: isDark
          ? `0 12px 28px ${alpha('#000', 0.28)}`
          : `0 14px 30px ${alpha('#0f172a', 0.07)}`,
        transition: 'transform 0.22s ease, box-shadow 0.22s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: isDark
            ? `0 16px 34px ${alpha('#000', 0.34)}`
            : `0 18px 36px ${alpha(color, 0.16)}`,
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: -30,
          right: -24,
          width: 90,
          height: 90,
          borderRadius: '50%',
          bgcolor: alpha(color, isDark ? 0.12 : 0.14),
          pointerEvents: 'none',
        }}
      />
      <Stack spacing={0.85} sx={{ p: 1.5, position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: '10px',
            display: 'grid',
            placeItems: 'center',
            color,
            background: `linear-gradient(145deg, ${alpha(color, 0.28)} 0%, ${alpha(color, 0.1)} 100%)`,
            border: `1px solid ${alpha(color, 0.35)}`,
            '& .MuiSvgIcon-root': { fontSize: 18 },
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography
            variant="overline"
            sx={{
              color: alpha(isDark ? '#e2e8f0' : '#475569', 0.9),
              fontWeight: 800,
              letterSpacing: '0.06em',
              fontSize: '0.62rem',
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              mt: 0.15,
              fontWeight: 900,
              letterSpacing: '-0.03em',
              color: isDark ? '#f8fafc' : '#0f172a',
              fontSize: { xs: '1.05rem', md: '1.18rem' },
              lineHeight: 1.15,
            }}
          >
            {value}
          </Typography>
          {subtitle ? (
            <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 600 }}>
              {subtitle}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Paper>
  );
}

function InfoRow({ icon, label, value, isDark }) {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
        alignItems: 'flex-start',
        py: 0.7,
        px: 1,
        borderRadius: 1.5,
        backgroundColor: isDark ? alpha('#94a3b8', 0.06) : alpha('#0f172a', 0.025),
      }}
    >
      <Box sx={{ color: HALL_ACCENT, mt: '2px' }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 700 }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ color: isDark ? '#e2e8f0' : '#334155', fontWeight: 600, wordBreak: 'break-word' }}>
          {value || '—'}
        </Typography>
      </Box>
    </Box>
  );
}

export default function HallVendorDetailPage() {
  const { vendorId } = useParams();
  const normalizedVendorId = useMemo(() => {
    const parsed = Number(vendorId);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }, [vendorId]);
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const themeMode = isDark ? 'dark' : 'light';
  const pageShellSx = getDashboardPageShellSx(themeMode);
  const surfaceTokens = getDashboardDarkSurfaceTokens(isDark);
  const accent = DASHBOARD_ACCENT;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);

  const loadDetail = useCallback(async () => {
    if (!normalizedVendorId) {
      setError('Vendor not found.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await getHallVendorDetail(normalizedVendorId);
      setPayload(data);
    } catch (err) {
      setPayload(null);
      setError(getHallApiErrorMessage(err, 'Unable to load vendor details.'));
    } finally {
      setLoading(false);
    }
  }, [normalizedVendorId]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const vendor = payload?.vendor || null;
  const summary = payload?.summary || {};
  const assignments = Array.isArray(payload?.assignments) ? payload.assignments : [];
  const payments = Array.isArray(payload?.payments) ? payload.payments : [];
  const services = parseServiceTags(vendor?.services);
  const locationLine = [vendor?.address, vendor?.city, vendor?.state, vendor?.country].filter(Boolean).join(', ');

  const contractTotal = roundHallMoney(summary.contract_total);
  const paidTotal = roundHallMoney(summary.paid_total);
  const dueTotal = roundHallMoney(summary.due_total);
  const includedTotal = roundHallMoney(summary.included_total);
  const settlementPct = contractTotal > 0
    ? Math.min(100, Math.round((paidTotal / contractTotal) * 100))
    : (paidTotal > 0 ? 100 : 0);

  const settlementPie = useMemo(() => {
    if (contractTotal <= 0 && paidTotal <= 0 && dueTotal <= 0) {
      return [{ name: 'No contracts', value: 1, color: alpha('#94a3b8', 0.45) }];
    }
    const rows = [
      { name: 'Paid', value: paidTotal, color: PROFIT },
      { name: 'Due', value: dueTotal, color: DUE_COLOR },
    ].filter((row) => row.value > 0);
    return rows.length ? rows : [{ name: 'Settled', value: 1, color: PROFIT }];
  }, [contractTotal, dueTotal, paidTotal]);

  const paymentMethodPie = useMemo(() => {
    if (!payments.length) {
      return [{ name: 'No payouts', value: 1, color: alpha('#94a3b8', 0.45) }];
    }
    const totals = {};
    payments.forEach((row) => {
      const key = String(row.mode || row.payment_method || 'other').replace(/_/g, ' ');
      totals[key] = roundHallMoney((totals[key] || 0) + (Number(row.amount) || 0));
    });
    return Object.entries(totals).map(([name, value], index) => ({
      name,
      value,
      color: METHOD_COLORS[index % METHOD_COLORS.length],
    }));
  }, [payments]);

  const eventContractChart = useMemo(() => {
    if (!assignments.length) return [];
    return assignments.slice(0, 8).map((row) => {
      const fullName = String(row.event_name || row.event_id || `Event ${row.booking_id}`);
      const name = fullName.length > 18 ? `${fullName.slice(0, 16)}…` : fullName;
      return {
        name,
        contract: roundHallMoney(row.contract_amount),
        paid: roundHallMoney(row.paid_amount),
        due: roundHallMoney(row.due_amount),
      };
    });
  }, [assignments]);

  const payoutTrendChart = useMemo(() => {
    if (!payments.length) return [];
    const buckets = {};
    payments.forEach((row) => {
      const stamp = row.paid_at || row.processed_at;
      const parsed = stamp && moment(stamp).isValid() ? moment(stamp) : null;
      const key = parsed ? parsed.format('YYYY-MM') : 'unknown';
      if (!buckets[key]) {
        buckets[key] = {
          sortKey: key,
          name: parsed ? parsed.format('MMM YY') : 'Unknown',
          payout: 0,
        };
      }
      buckets[key].payout = roundHallMoney(buckets[key].payout + (Number(row.amount) || 0));
    });
    return Object.values(buckets)
      .sort((a, b) => String(a.sortKey).localeCompare(String(b.sortKey)))
      .slice(-8)
      .map(({ name, payout }) => ({ name, payout }));
  }, [payments]);

  const eventBarKeys = useMemo(() => ([
    { dataKey: 'contract', name: 'Contract', color: SECONDARY },
    { dataKey: 'paid', name: 'Paid', color: HALL_ACCENT },
    { dataKey: 'due', name: 'Due', color: DUE_COLOR },
  ]), []);

  const modernPanelCardSx = {
    mb: 2,
    borderRadius: 2.5,
    border: `1px solid ${surfaceTokens.surfaceBorder}`,
    background: isDark ? surfaceTokens.surfaceGradient : '#ffffff',
    boxShadow: isDark ? 'inset 0 1px 0 rgba(255,255,255,0.04)' : 'none',
    overflow: 'hidden',
  };

  const eventColumns = useMemo(() => ([
    {
      field: 'event_id',
      label: 'Event ID',
      sortable: false,
      render: (_value, row) => (
        <Typography
          component="span"
          onClick={(event) => {
            event.stopPropagation();
            if (row.booking_id) navigate(`/hall/event/${row.booking_id}/`);
          }}
          sx={{
            color: HALL_ACCENT,
            fontWeight: 800,
            fontSize: '0.82rem',
            cursor: row.booking_id ? 'pointer' : 'default',
            '&:hover': row.booking_id ? { textDecoration: 'underline' } : undefined,
          }}
        >
          {row.event_id || row.booking_code || '—'}
        </Typography>
      ),
    },
    {
      field: 'event_name',
      label: 'Event',
      sortable: false,
      render: (_value, row) => (
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '0.86rem' }}>{row.event_name || '—'}</Typography>
          <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
            {row.hall_name || 'Hall'} · {row.customer_name || 'Guest'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'event_date',
      label: 'Date',
      sortable: false,
      render: (_value, row) => formatDate(row.event_date),
    },
    {
      field: 'task',
      label: 'Work assignment',
      sortable: false,
      render: (_value, row) => row.task?.trim() || '—',
    },
    {
      field: 'contract_amount',
      label: 'Total',
      sortable: false,
      render: (_value, row) => formatHallCurrency(row.contract_amount),
    },
    {
      field: 'paid_amount',
      label: 'Paid',
      sortable: false,
      render: (_value, row) => formatHallCurrency(row.paid_amount),
    },
    {
      field: 'due_amount',
      label: 'Due',
      sortable: false,
      render: (_value, row) => (
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: '0.84rem',
            color: roundHallMoney(row.due_amount) > 0
              ? (isDark ? '#fbbf24' : '#c2410c')
              : (isDark ? '#86efac' : '#15803d'),
          }}
        >
          {formatHallCurrency(row.due_amount)}
        </Typography>
      ),
    },
    {
      field: 'include_in_hall',
      label: 'Hall',
      sortable: false,
      render: (_value, row) => (
        <Chip
          size="small"
          label={row.include_in_hall ? 'Included' : 'Excluded'}
          sx={{
            height: 22,
            fontWeight: 800,
            fontSize: '0.66rem',
            ...statusChipSx(row.include_in_hall ? 'active' : 'tentative', isDark),
          }}
        />
      ),
    },
    {
      field: 'booking_status',
      label: 'Status',
      sortable: false,
      render: (_value, row) => (
        <Chip
          size="small"
          label={String(row.booking_status || '—').replace(/_/g, ' ')}
          sx={{ height: 22, fontSize: '0.68rem', ...statusChipSx(row.booking_status, isDark) }}
        />
      ),
    },
  ]), [isDark, navigate]);

  const paymentColumns = useMemo(() => ([
    {
      field: 'paid_at',
      label: 'Paid at',
      sortable: false,
      render: (_value, row) => formatDateTime(row.paid_at || row.processed_at),
    },
    {
      field: 'event_id',
      label: 'Event',
      sortable: false,
      render: (_value, row) => (
        <Box>
          <Typography
            component="span"
            onClick={(event) => {
              event.stopPropagation();
              if (row.booking_id) navigate(`/hall/event/${row.booking_id}/`);
            }}
            sx={{
              color: HALL_ACCENT,
              fontWeight: 800,
              fontSize: '0.8rem',
              cursor: row.booking_id ? 'pointer' : 'default',
              '&:hover': row.booking_id ? { textDecoration: 'underline' } : undefined,
            }}
          >
            {row.event_id || row.booking_code || '—'}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', color: isDark ? '#94a3b8' : '#64748b' }}>
            {row.event_name || 'Event'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'amount',
      label: 'Amount',
      sortable: false,
      render: (_value, row) => (
        <Typography sx={{ fontWeight: 800, color: isDark ? '#86efac' : '#15803d' }}>
          {formatHallCurrency(row.amount)}
        </Typography>
      ),
    },
    {
      field: 'payment_type',
      label: 'Type',
      sortable: false,
      render: (_value, row) => row.payment_type_label || String(row.payment_type || '—').replace(/_/g, ' '),
    },
    {
      field: 'mode',
      label: 'Mode',
      sortable: false,
      render: (_value, row) => String(row.mode || row.payment_method || '—').replace(/_/g, ' '),
    },
    {
      field: 'reference',
      label: 'Reference',
      sortable: false,
      render: (_value, row) => row.reference || row.reference_number || '—',
    },
  ]), [isDark, navigate]);

  if (loading) {
    return (
      <Box sx={pageShellSx}>
        <Box sx={{ mb: 2.5 }}>
          <HallModuleNav />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
          <CircularProgress sx={{ color: accent }} />
        </Box>
      </Box>
    );
  }

  if (error || !vendor) {
    return (
      <Box sx={pageShellSx}>
        <Box sx={{ mb: 2.5 }}>
          <HallModuleNav />
        </Box>
        <Paper sx={{ ...modernPanelCardSx, p: 3, textAlign: 'center' }}>
          <Typography color="error">{error || 'Vendor not found'}</Typography>
          <Button sx={{ mt: 2 }} onClick={() => navigate('/hall/vendor-management/')}>
            Back to Vendor Management
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ ...pageShellSx, width: '100%', boxSizing: 'border-box' }}>
      <Box sx={{ mb: 2.5 }}>
        <HallModuleNav />
      </Box>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.75 }}>
        <Button
          size="small"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/hall/vendor-management/')}
          sx={{ textTransform: 'none', fontWeight: 700, color: isDark ? '#cbd5e1' : '#334155' }}
        >
          Vendors
        </Button>
        <Typography variant="caption" sx={{ color: isDark ? '#64748b' : '#94a3b8', fontWeight: 700 }}>
          / {vendor.vendor_code || `Vendor #${vendor.id}`}
        </Typography>
      </Stack>

      {/* Hero */}
      <Paper
        elevation={0}
        sx={{
          ...modernPanelCardSx,
          p: 0,
          background: isDark
            ? `linear-gradient(135deg, ${alpha('#0b1f1a', 0.98)} 0%, ${alpha('#0f172a', 0.98)} 55%, ${alpha('#082f2b', 0.95)} 100%)`
            : `linear-gradient(135deg, #f0fdf8 0%, #ffffff 45%, #eefbf6 100%)`,
        }}
      >
        <Box
          sx={{
            height: 8,
            background: `linear-gradient(90deg, ${HALL_ACCENT} 0%, ${alpha('#0ea5a4', 0.85)} 55%, ${alpha(SECONDARY, 0.7)} 100%)`,
          }}
        />
        <Box sx={{ p: { xs: 1.75, md: 2.25 } }}>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <Avatar
              sx={{
                width: 64,
                height: 64,
                fontWeight: 900,
                fontSize: '1.25rem',
                bgcolor: alpha(HALL_ACCENT, isDark ? 0.9 : 1),
                color: '#04120e',
                boxShadow: `0 10px 24px ${alpha(HALL_ACCENT, 0.28)}, 0 0 0 4px ${alpha(HALL_ACCENT, 0.16)}`,
              }}
            >
              {vendorInitials(vendor.name)}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 220 }}>
              <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.35rem', md: '1.55rem' }, color: isDark ? '#f8fafc' : '#0f172a', letterSpacing: '-0.02em' }}>
                {vendor.name || 'Unnamed vendor'}
              </Typography>
              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mt: 0.7 }}>
                <Chip
                  size="small"
                  label={vendor.vendor_code || `Vendor #${vendor.id}`}
                  sx={{
                    height: 24,
                    fontWeight: 800,
                    color: '#04120e',
                    bgcolor: HALL_ACCENT,
                  }}
                />
                <Chip
                  size="small"
                  label={vendor.vendor_type_label || vendor.vendor_type || 'Vendor'}
                  sx={{ height: 24, fontWeight: 800, bgcolor: alpha(HALL_ACCENT, isDark ? 0.16 : 0.12) }}
                />
                <Chip
                  size="small"
                  label={vendor.status || (vendor.is_active ? 'active' : 'inactive')}
                  sx={{ height: 24, fontSize: '0.68rem', ...statusChipSx(vendor.status || (vendor.is_active ? 'active' : 'inactive'), isDark) }}
                />
              </Stack>
            </Box>
            <Box sx={{ minWidth: { xs: '100%', sm: 220 }, flex: { sm: '0 0 240px' } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 800 }}>
                  Settlement progress
                </Typography>
                <Typography variant="caption" sx={{ color: isDark ? '#e2e8f0' : '#0f172a', fontWeight: 900 }}>
                  {settlementPct}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={settlementPct}
                sx={{
                  height: 8,
                  borderRadius: 99,
                  backgroundColor: isDark ? alpha('#94a3b8', 0.18) : alpha('#0f172a', 0.08),
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 99,
                    background: dueTotal > 0
                      ? `linear-gradient(90deg, ${HALL_ACCENT}, ${WARNING})`
                      : `linear-gradient(90deg, ${HALL_ACCENT}, ${PROFIT})`,
                  },
                }}
              />
              <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 600, mt: 0.55, display: 'block' }}>
                {formatHallCurrency(paidTotal)} paid of {formatHallCurrency(contractTotal)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* KPI row */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
          gap: 1.35,
          mb: 2,
        }}
      >
        <MetricTile
          title="Events"
          value={summary.event_count || 0}
          subtitle="Assigned hall events"
          icon={<EventNoteOutlinedIcon />}
          color={HALL_ACCENT}
          isDark={isDark}
        />
        <MetricTile
          title="Contract"
          value={formatHallCurrency(contractTotal)}
          subtitle={`${formatHallCurrency(includedTotal)} in hall bill`}
          icon={<PaidOutlinedIcon />}
          color={SECONDARY}
          isDark={isDark}
        />
        <MetricTile
          title="Paid out"
          value={formatHallCurrency(paidTotal)}
          subtitle={`${summary.payment_count || 0} payouts`}
          icon={<AccountBalanceWalletOutlinedIcon />}
          color={PROFIT}
          isDark={isDark}
        />
        <MetricTile
          title="Outstanding"
          value={formatHallCurrency(dueTotal)}
          subtitle={dueTotal > 0 ? 'Vendor due remaining' : 'Fully settled'}
          icon={<RequestQuoteOutlinedIcon />}
          color={dueTotal > 0 ? DUE_COLOR : PROFIT}
          isDark={isDark}
        />
      </Box>

      {/* Analytics charts */}
      <Paper
        elevation={0}
        sx={{
          ...modernPanelCardSx,
          p: { xs: 1.5, md: 2.1 },
          background: isDark
            ? `linear-gradient(145deg, ${alpha('#0b1524', 0.98)} 0%, ${alpha('#0f172a', 0.98)} 100%)`
            : 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
        }}
      >
        <Stack spacing={0.35} sx={{ mb: 1.75 }}>
          <Typography
            variant="overline"
            sx={{ color: HALL_ACCENT, fontWeight: 900, letterSpacing: '0.12em' }}
          >
            Vendor analytics
          </Typography>
          <Typography sx={{ fontWeight: 900, color: isDark ? '#f8fafc' : '#0f172a', fontSize: '1.15rem' }}>
            Settlement & payout insights
          </Typography>
          <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
            Visual breakdown of contracts, dues, payment modes, and event-level performance.
          </Typography>
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 1.5,
            mb: 1.5,
          }}
        >
          <DashboardChartCard
            variant="performance"
            eyebrow="SETTLEMENT"
            title=""
            subtitle="Paid vs outstanding due"
            chartType="pie"
            pieData={settlementPie}
            pieInnerRadius={58}
            theme={themeMode}
            height={270}
            formatValue={(value) => formatHallCurrency(value)}
            colors={[PROFIT, DUE_COLOR]}
          />
          <DashboardChartCard
            variant="performance"
            eyebrow="PAYOUT MIX"
            title=""
            subtitle={payments.length ? 'Distribution by payment mode' : 'No payouts recorded yet'}
            chartType="pie"
            pieData={paymentMethodPie}
            pieInnerRadius={58}
            theme={themeMode}
            height={270}
            formatValue={(value) => formatHallCurrency(value)}
            colors={METHOD_COLORS}
          />
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '1.2fr 1fr' },
            gap: 1.5,
          }}
        >
          <DashboardChartCard
            variant="performance"
            eyebrow="BY EVENT"
            title=""
            subtitle={eventContractChart.length ? 'Contract · Paid · Due' : 'No assigned events yet'}
            chartType="bar"
            data={eventContractChart}
            dataKeys={eventBarKeys}
            colors={[SECONDARY, HALL_ACCENT, DUE_COLOR]}
            theme={themeMode}
            height={300}
            formatValue={(value) => formatHallCurrency(value)}
            showBarTotalTooltip
            barTotalLabel="Total"
          />
          <DashboardChartCard
            variant="performance"
            eyebrow="TREND"
            title=""
            subtitle={payoutTrendChart.length ? 'Vendor payouts over time' : 'No payout history yet'}
            chartType="area"
            showArea
            data={payoutTrendChart}
            dataKeys={[{ dataKey: 'payout', name: 'Payout', color: HALL_ACCENT }]}
            colors={[HALL_ACCENT]}
            theme={themeMode}
            height={300}
            formatValue={(value) => formatHallCurrency(value)}
          />
        </Box>

        {payments.length > 0 ? (
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
            {paymentMethodPie.map((row) => (
              <Chip
                key={row.name}
                size="small"
                label={`${row.name}: ${formatHallCurrency(row.value)}`}
                sx={{
                  fontWeight: 700,
                  color: isDark ? '#e2e8f0' : '#0f172a',
                  bgcolor: alpha(row.color || HALL_ACCENT, isDark ? 0.18 : 0.14),
                  border: `1px solid ${alpha(row.color || HALL_ACCENT, 0.35)}`,
                }}
              />
            ))}
          </Stack>
        ) : null}
      </Paper>

      {/* Profile + services */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.15fr 1fr' },
          gap: 2,
          mb: 2,
        }}
      >
        <Paper elevation={0} sx={{ ...modernPanelCardSx, mb: 0, p: { xs: 1.5, md: 2 } }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
            <StorefrontIcon sx={{ color: HALL_ACCENT }} />
            <Typography sx={{ fontWeight: 900, color: isDark ? '#f8fafc' : '#0f172a' }}>
              Contact & profile
            </Typography>
          </Stack>
          <Stack spacing={0.75}>
            <InfoRow icon={<PhoneIcon sx={{ fontSize: 16 }} />} label="Phone" value={formatPhone(vendor)} isDark={isDark} />
            <InfoRow icon={<EmailIcon sx={{ fontSize: 16 }} />} label="Email" value={vendor.email} isDark={isDark} />
            <InfoRow icon={<LocationOnIcon sx={{ fontSize: 16 }} />} label="Address" value={locationLine || '—'} isDark={isDark} />
            <InfoRow icon={<StorefrontIcon sx={{ fontSize: 16 }} />} label="GSTIN" value={vendor.gstin} isDark={isDark} />
          </Stack>
          {vendor.notes ? (
            <Box sx={{ mt: 1.25, p: 1.1, borderRadius: 2, bgcolor: isDark ? alpha('#94a3b8', 0.08) : alpha('#0f172a', 0.03) }}>
              <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 800 }}>
                Notes
              </Typography>
              <Typography variant="body2" sx={{ color: isDark ? '#cbd5e1' : '#475569', whiteSpace: 'pre-line', mt: 0.35 }}>
                {vendor.notes}
              </Typography>
            </Box>
          ) : null}
        </Paper>

        <Paper elevation={0} sx={{ ...modernPanelCardSx, mb: 0, p: { xs: 1.5, md: 2 } }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
            <TrendingUpOutlinedIcon sx={{ color: HALL_ACCENT }} />
            <Typography sx={{ fontWeight: 900, color: isDark ? '#f8fafc' : '#0f172a' }}>
              Services & hall billing
            </Typography>
          </Stack>
          {services.length ? (
            <Stack direction="row" spacing={0.65} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
              {services.map((tag) => (
                <Chip
                  key={tag}
                  size="small"
                  label={tag}
                  sx={{
                    fontWeight: 700,
                    bgcolor: alpha(HALL_ACCENT, isDark ? 0.14 : 0.1),
                    border: `1px solid ${alpha(HALL_ACCENT, 0.28)}`,
                  }}
                />
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#64748b', mb: 1.5 }}>
              No services listed.
            </Typography>
          )}
          <Box
            sx={{
              p: 1.25,
              borderRadius: 2,
              border: `1px solid ${alpha(HALL_ACCENT, isDark ? 0.28 : 0.22)}`,
              background: isDark
                ? `linear-gradient(145deg, ${alpha('#0f172a', 0.7)}, ${alpha(HALL_ACCENT, 0.08)})`
                : `linear-gradient(145deg, #ffffff, ${alpha(HALL_ACCENT, 0.08)})`,
            }}
          >
            <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 800 }}>
              Included in hall collection
            </Typography>
            <Typography sx={{ fontWeight: 900, fontSize: '1.2rem', color: isDark ? '#f8fafc' : '#0f172a' }}>
              {formatHallCurrency(includedTotal)}
            </Typography>
            <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 600 }}>
              Contracts marked include-in-hall across assigned events
            </Typography>
          </Box>
        </Paper>
      </Box>

      <Paper elevation={0} sx={{ ...modernPanelCardSx, p: { xs: 1.5, md: 2 } }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
          <EventIcon sx={{ color: HALL_ACCENT }} />
          <Typography sx={{ fontWeight: 900, color: isDark ? '#f8fafc' : '#0f172a' }}>
            Events handled
          </Typography>
          <Chip size="small" label={assignments.length} sx={{ height: 22, fontWeight: 800 }} />
        </Stack>
        <DataTable
          columns={eventColumns}
          data={assignments}
          loading={false}
          emptyMessage="This vendor is not assigned to any hall events yet."
          stickyHeader
        />
      </Paper>

      <Paper elevation={0} sx={{ ...modernPanelCardSx, p: { xs: 1.5, md: 2 } }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
          <PaymentsIcon sx={{ color: HALL_ACCENT }} />
          <Typography sx={{ fontWeight: 900, color: isDark ? '#f8fafc' : '#0f172a' }}>
            Payment history
          </Typography>
          <Chip size="small" label={payments.length} sx={{ height: 22, fontWeight: 800 }} />
        </Stack>
        <DataTable
          columns={paymentColumns}
          data={payments}
          loading={false}
          emptyMessage="No vendor payouts recorded yet."
          stickyHeader
        />
      </Paper>
    </Box>
  );
}
